import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { CREATE_TYPE_WITH_LIMIT_DIALOG, CREATE_PRODUCT_ATTRIBUTE_DIALOG, UPDATE_PRODUCT_ATTRIBUTE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import { PostTypePayloadRequest } from '../../../../../core/modules/master/interfaces/post-type';
import { TypeLabel } from '../categories/CreateDialog';
import { ERROR } from '../../../../../core/constants/errorCode';

interface Props {
  type: tType
}

const schema = yup.object().shape({
  description: yup.string().test('len', '3 carácteres requeridos', val => val?.length === 3).required('Campo requerido'),
  name: yup.string().required('Campo requerido'),
});

const CreateDialogWithLimit = (props: Props) => {
  const { type } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const {
    errorCode,
    postType,
    getTypes,
    productTypes,
    warehouseTypes,
    locationTypes,
    containerTypes,
    channelTypes,
    acquisitionTypes,
    receptionTypes,
  } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);
  const [typeNameError, setTypeNameError] = useState('');

  const { control, formState, handleSubmit, resetField } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    resetField('name');
    resetField('description');
    setTypeNameError('');
    closeDialog(CREATE_TYPE_WITH_LIMIT_DIALOG);
  }, [closeDialog, resetField]);

  const getExistingTypes = useCallback(() => {
    switch (type) {
      case 'product':
        return productTypes;
      case 'warehouse':
        return warehouseTypes;
      case 'location':
        return locationTypes;
      case 'container':
        return containerTypes;
      case 'channel':
        return channelTypes;
      case 'acquisition':
        return acquisitionTypes;
      case 'reception':
        return receptionTypes;
      default:
        return [];
    }
  }, [type, productTypes, warehouseTypes, locationTypes, containerTypes, channelTypes, acquisitionTypes, receptionTypes]);

  const onSubmit = useCallback(
    (values: PostTypePayloadRequest) => {
      const exists = getExistingTypes().some(t => t.description?.toLowerCase().trim() === values.description?.toLowerCase().trim());
      if (exists) {
        setTypeNameError('Ya existe un tipo con esta sigla');
        return;
      }
      if (companyId) {
        setLoading(true);
        postType(companyId, values, type, () => {
          resetForm();
          getTypes(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, postType, type, resetForm, getTypes, getExistingTypes]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_TYPE_WITH_LIMIT_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Tipo de {TypeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sigla"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description || !!typeNameError}
                          helperText={errors?.description?.message || typeNameError}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                            const exists = getExistingTypes().some(t => t.description?.toLowerCase().trim() === value.toLowerCase().trim());
                            setTypeNameError(exists ? 'Ya existe un tipo con esta sigla' : '');
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                  </div>

                  {errorCode === ERROR.ERR_409 && (
                    <Typography color="error" className="text-md text-center mb-24">
                      Ya existe un tipo con esa sigla
                    </Typography>
                  )}

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || !!typeNameError || loading || (Array.isArray(dialogs) && (dialogs.includes(CREATE_PRODUCT_ATTRIBUTE_DIALOG) || dialogs.includes(UPDATE_PRODUCT_ATTRIBUTE_DIALOG)))}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialogWithLimit;
